import { useMemo, useState } from 'react'
import {
  Button,
  IconArrowUpRight,
  IconPlus,
  IndigoIndigo60,
  InputSelectControl,
  Label,
  Link,
  Primary13Regular,
  Switch,
  Modal,
  useModalState,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { MAPPABLE_LEDGER_ACCOUNT_LABELS } from '../xeroLedgerAccountMappings.constants'
import type {
  MappableLedgerAccount,
  XeroLedgerAccount
} from '../xeroLedgerAccountMappings.types'

interface Props {
  mappableLedgerAccount: MappableLedgerAccount
  xeroLedgerAccounts: XeroLedgerAccount[]
  existingMappingCodes: string[]
  onCreate: (params: {
    mappableLedgerAccount: MappableLedgerAccount
    xeroLedgerAccountCode: string
    xeroLedgerAccountName: string
    isDefault: boolean
  }) => Promise<void>
  existingMappingCount: number
}

const filterByLabelOrCode = (
  groups: {
    label: string | null
    options: { label: string; value: string }[]
  }[],
  searchTerm: string
) => {
  const term = searchTerm.toLowerCase()

  return groups.reduce<typeof groups>((acc, group) => {
    const filteredOptions = group.options.filter(
      option =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term)
    )

    if (filteredOptions.length === 0) {
      return acc
    }

    return [...acc, { ...group, options: filteredOptions }]
  }, [])
}

const AddXeroLedgerAccountMappingModalUI = ({
  mappableLedgerAccount,
  xeroLedgerAccounts,
  existingMappingCodes,
  onCreate,
  existingMappingCount,
  onClose
}: Props & { onClose: () => void }) => {
  const [isDefault, setIsDefault] = useState(existingMappingCount === 0)

  const { fields, queries } = useForm({
    value: {
      xeroLedgerAccountCode: ''
    },
    fieldConfiguration: [
      {
        property: 'xeroLedgerAccountCode',
        validation: [required]
      }
    ]
  })

  const selectedAccount = useMemo(
    () =>
      xeroLedgerAccounts.find(
        a => a.code === fields.xeroLedgerAccountCode.value
      ),
    [xeroLedgerAccounts, fields.xeroLedgerAccountCode.value]
  )

  const options = xeroLedgerAccounts
    .filter(account => !existingMappingCodes.includes(account.code))
    .map(account => ({
      label: `${account.name} (${account.code})`,
      value: account.code
    }))

  const canSubmit = queries.isValid && selectedAccount

  const handleSubmit = () => {
    if (selectedAccount) {
      void onCreate({
        mappableLedgerAccount,
        xeroLedgerAccountCode: selectedAccount.code,
        xeroLedgerAccountName: selectedAccount.name,
        isDefault
      })
        .then(onClose)
        .catch(() => {})
    }
  }

  const ledgerAccountLabel =
    MAPPABLE_LEDGER_ACCOUNT_LABELS[mappableLedgerAccount]

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        {`Add ${ledgerAccountLabel.toLowerCase()} account mapping`}
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3}>
          <Flex flexDirection="column" gap={1}>
            <Flex justifyContent="space-between" alignItems="center">
              <Label size="sm">Xero ledger account</Label>
              <Link
                display="flex"
                alignItems="center"
                to="https://go.xero.com/GeneralLedger/ChartOfAccounts.aspx"
                isExternal
                {...Primary13Regular}
                color={IndigoIndigo60}
                pb={2}
              >
                View chart of accounts on Xero
                <IconArrowUpRight
                  style={{ display: 'inline', marginLeft: '4px' }}
                />
              </Link>
            </Flex>
            <InputSelectControl
              id="xero-ledger-account"
              data-testid="xeroLedgerAccountMapping.account"
              placeholder="Select a Xero ledger account"
              options={options}
              filterOptionGroups={filterByLabelOrCode}
              onChange={fields.xeroLedgerAccountCode.onChange}
            />
          </Flex>
          <Flex alignItems="center" justifyContent="space-between">
            <Label size="sm">Set as default</Label>
            <Switch
              size="md"
              id="xero-mapping-default"
              as="div"
              data-testid="xeroLedgerAccountMapping.isDefault"
              isChecked={isDefault}
              onChange={setIsDefault}
            />
          </Flex>
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Add mapping
        </Button>
      </Modal.Footer>
    </>
  )
}

export const AddXeroLedgerAccountMappingModal = (props: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        variant="ghost"
        onClick={openModal}
        leadingIcon={<IconPlus color="inherit" />}
      >
        Add mapping
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="xeroLedgerAccountMappingModal"
      >
        <AddXeroLedgerAccountMappingModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}
